/**
 * @fileoverview Shared Calorie schema used by both frontend and backend. A day
 * can hold several entries (one per meal or snack); consumers sum them into a
 * daily total.
 */

import { z } from 'zod';
import { commonValidators, createCrudSchemas, createEntityIdSchema, createdAtSchema, deletedAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { ID_PATTERNS } from './types.js';

/** Upper bound for a single entry — matches the `calorie_calories_range` check. */
export const MAX_CALORIES_PER_ENTRY = 20000;

/**
 * Calorie validation schema
 */
export const CalorieSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.CALORIE, 'ID must be in kc-prefixed nanoid7 format'),
  recorded_date: commonValidators.dateString(),
  calories: z.number()
    .int('Calories must be a whole number')
    .min(1, 'Calories must be greater than 0')
    .max(MAX_CALORIES_PER_ENTRY, `Calories must be ${MAX_CALORIES_PER_ENTRY} or less`),
  description: commonValidators.optionalString(255),
  metadata: commonValidators.jsonField('Metadata'),
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(CalorieSchema);

export const CalorieCreateSchema = CreateSchema;
export const CalorieUpdateSchema = UpdateSchema;

export type Calorie = z.infer<typeof CalorieSchema>;
export type CalorieCreate = z.infer<typeof CalorieCreateSchema>;
export type CalorieUpdate = z.infer<typeof CalorieUpdateSchema>;

export const CALORIE_FIELDS = getSchemaFields(CalorieSchema);
